import { readFile } from "node:fs/promises";
import { join } from "node:path";
import { GitClient } from "../git/GitClient.js";
import { LockfileManager } from "../LockfileManager.js";
import { ReplayApplicator } from "../ReplayApplicator.js";
import { isBinaryFile } from "../shared/binary.js";
import { readFernignorePatterns } from "../shared/fernignore.js";
import { ReplayCommitter } from "../ReplayCommitter.js";
import { ReplayDetector } from "../ReplayDetector.js";
import { computePerPatchDiffWithFallback } from "../PatchRegionDiff.js";

export interface ResolveOptions {
    /** Check for remaining conflict markers before committing. Default: true */
    checkMarkers?: boolean;
}

export interface ResolveResult {
    /** Whether the resolve succeeded */
    success: boolean;
    /** Commit SHA of the [fern-replay] commit, if created */
    commitSha?: string;
    /** Reason for failure or current state */
    reason?: string;
    /** Files that have conflict markers */
    unresolvedFiles?: string[];
    /** Phase the command executed */
    phase?: "applied" | "committed" | "nothing-to-resolve";
    /** Number of patches applied to working tree (phase 1) */
    patchesApplied?: number;
    /** Number of patches resolved and committed (phase 2) */
    patchesResolved?: number;
    /**
     * Patches permanently dismissed during phase 2 — the customer kept the
     * generator's version for every file in the patch. These are tombstoned
     * in the lockfile and will not be re-detected on future generations.
     */
    patchesDismissed?: Array<{ id: string; message: string }>;
    /** Non-fatal diagnostic messages (e.g., per-patch fallbacks) */
    warnings?: string[];
}

export async function resolve(outputDir: string, options?: ResolveOptions): Promise<ResolveResult> {
    const lockManager = new LockfileManager(outputDir);

    if (!lockManager.exists()) {
        return { success: false, reason: "no-lockfile" };
    }

    const lock = lockManager.read();
    if (lock.patches.length === 0) {
        return { success: false, reason: "no-patches" };
    }

    const git = new GitClient(outputDir);
    const unresolvedPatches = lockManager.getUnresolvedPatches();
    const resolvingPatches = lockManager.getResolvingPatches();

    // ---- Phase 1: Apply unresolved patches to working tree ----
    if (unresolvedPatches.length > 0) {
        const applicator = new ReplayApplicator(git, lockManager, outputDir);
        // applyMode: "resolve" — preserve conflict markers on disk so the
        // customer sees A's conflicts (instead of having them silently
        // stripped by the intra-loop sanitize that the replay pipeline
        // depends on for clean follow-up patches). See ReplayApplicator
        // applyPatches docstring for the rationale.
        await applicator.applyPatches(unresolvedPatches, { applyMode: "resolve" });

        const markerFiles = await findConflictMarkerFiles(git);

        if (markerFiles.length > 0) {
            // Mark as "resolving" so next call knows Phase 1 happened
            for (const patch of unresolvedPatches) {
                lockManager.updatePatch(patch.id, { status: "resolving" });
            }
            lockManager.save();

            return {
                success: false,
                reason: "conflicts-applied",
                unresolvedFiles: markerFiles,
                phase: "applied",
                patchesApplied: unresolvedPatches.length
            };
        }

        // All patches applied cleanly — fall through to commit phase.
        // Treat these as needing commit (same as resolving patches).
    }

    // ---- Phase 1.5: Conflict markers still present ----
    if (options?.checkMarkers !== false) {
        const currentMarkerFiles = await findConflictMarkerFiles(git);
        if (currentMarkerFiles.length > 0) {
            return { success: false, reason: "unresolved-conflicts", unresolvedFiles: currentMarkerFiles };
        }
    }

    // ---- Phase 2: Commit resolution ----
    // Includes both "resolving" patches (from a previous Phase 1 call) and
    // "unresolved" patches that applied cleanly (from Phase 1 above).
    const patchesToCommit = [...resolvingPatches, ...unresolvedPatches];
    if (patchesToCommit.length > 0) {
        const currentGen = lock.current_generation;
        // The detector must see the same `.fernignore` patterns production
        // detection uses — they scope patch materialization, and the
        // rejection tombstone below recomputes re-emission hashes through it.
        const detector = new ReplayDetector(git, lockManager, outputDir, readFernignorePatterns(outputDir));
        const warnings: string[] = [];
        const patchesDismissed: Array<{ id: string; message: string }> = [];
        let patchesResolved = 0;

        // Two-pass: pass 1 computes per-patch attribution; pass 2 applies
        // it with consolidation when multiple patches end up with byte-
        // identical content (which would otherwise duplicate-apply at the
        // next regen and corrupt the customer's file).
        type PassResult =
            | { kind: "skip" }
            | { kind: "revert" }
            | { kind: "apply"; diff: string; hash: string; hadFallback: boolean; fallbackFiles: string[] };

        const pass1: PassResult[] = [];
        for (const patch of patchesToCommit) {
            // Per-patch contribution diff. Using a cumulative
            // `git diff currentGen -- patch.files` would assign byte-
            // identical patch_content to every patch sharing a file —
            // which the consolidation pass below catches, but per-patch
            // attribution (when achievable) preserves better provenance.
            const result = await computePerPatchDiffWithFallback(
                patch,
                (f) => git.showFile(currentGen, f),
                (f) => readFile(join(outputDir, f), "utf-8").catch(() => null),
                (files) => git.exec(["diff", currentGen, "--", ...files]).catch(() => null)
            );

            if (result === null) {
                warnings.push(
                    `Patch ${patch.id}: cumulative-diff fallback failed for unlocatable files; preserving patch unchanged`
                );
                pass1.push({ kind: "skip" });
                continue;
            }

            const diff = result.diff;
            if (!diff.trim()) {
                pass1.push({ kind: "revert" });
                continue;
            }

            const hash = detector.computeContentHash(diff);
            pass1.push({
                kind: "apply",
                diff,
                hash,
                hadFallback: result.hadFallback,
                fallbackFiles: result.fallbackFiles,
            });
        }

        // Pass 2 setup: identify the LAST occurrence of each content hash —
        // that's the canonical patch (most recent customer intent). Earlier
        // patches with the same hash get consolidated into that one. Per
        // user direction (criterion #5 in the brief): "consolidate to ONE
        // canonical patch rather than copying cumulative content into all."
        const lastIndexByHash = new Map<string, number>();
        for (let i = 0; i < pass1.length; i++) {
            const r = pass1[i]!;
            if (r.kind === "apply") lastIndexByHash.set(r.hash, i);
        }

        for (let i = 0; i < patchesToCommit.length; i++) {
            const patch = patchesToCommit[i]!;
            const r = pass1[i]!;
            if (r.kind === "skip") continue;
            if (r.kind === "revert") {
                // Rejection: the customer kept the generator's version for
                // every file in the patch. Tombstone the patch so the detector
                // doesn't re-emit the original commit on the next regeneration
                // (merge commits of regen PRs keep the commit inside the scan
                // range forever).
                //
                // Two hashes are tombstoned: the stored content_hash, and the
                // re-emission hash recomputed from the original commit through
                // the exact detection pipeline. The stored hash alone is not
                // enough — it is rewritten every time the patch is carried
                // forward across a clean regen, so it no longer matches what
                // detection will compute when the commit re-enters the scan
                // range.
                const hashes = [patch.content_hash];
                if (patch.original_commit) {
                    const materialized = await detector.materializeCommitPatch(patch.original_commit);
                    if (materialized.status === "ok" && !hashes.includes(materialized.contentHash)) {
                        hashes.push(materialized.contentHash);
                    } else if (materialized.status === "unreachable") {
                        warnings.push(
                            `Patch ${patch.id}: original commit ${patch.original_commit.slice(0, 7)} is ` +
                                `unreachable; dismissed using the stored content hash only. If this commit ` +
                                `reappears in history, the customization may be re-detected once.`
                        );
                    }
                }
                lockManager.addDismissal({
                    patch_id: patch.id,
                    original_commit: patch.original_commit,
                    original_message: patch.original_message,
                    content_hashes: hashes,
                    dismissed_at: new Date().toISOString(),
                    via: "resolve",
                });
                lockManager.removePatch(patch.id);
                patchesDismissed.push({ id: patch.id, message: patch.original_message });
                continue;
            }

            // r.kind === "apply"
            if (lastIndexByHash.get(r.hash) !== i) {
                // Duplicate of a later patch — consolidate by removing.
                // The customer's contribution survives on the canonical
                // patch (the last one with this hash). Per-commit
                // attribution is recoverable from `git log -- <file>`.
                const canonicalIdx = lastIndexByHash.get(r.hash)!;
                const canonical = patchesToCommit[canonicalIdx]!;
                lockManager.removePatch(patch.id);
                warnings.push(
                    `Consolidated patch ${patch.id} (commit ${(patch.original_commit || "<missing>").slice(0, 7)}) ` +
                        `into patch ${canonical.id} (commit ${(canonical.original_commit || "<missing>").slice(0, 7)}): ` +
                        `byte-identical patch_content after resolution. Per-commit attribution preserved in git log; ` +
                        `lockfile collapsed to avoid duplicate-apply corruption on next regen.`
                );
                continue;
            }

            if (r.hadFallback) {
                warnings.push(
                    `Patch ${patch.id}: ${r.fallbackFiles.join(", ")} could not be anchored; cumulative-diff fallback applied for those files`
                );
            }

            const changedFiles = r.hadFallback
                ? await getChangedFiles(git, currentGen, patch.files)
                : extractFilesFromDiff(r.diff);

            // Capture THEIRS as a snapshot of the customer's resolved file
            // content. This is what THEIRS *means* — the post-customer-edit
            // state. Storing it directly (instead of relying on
            // patch_content's diff to reconstruct it) makes future regens
            // robust to generator structural changes that would invalidate
            // the diff's line anchors.
            const filesForSnapshot = changedFiles.length > 0 ? changedFiles : patch.files;
            const theirsSnapshot: Record<string, string> = {};
            for (const file of filesForSnapshot) {
                if (isBinaryFile(file)) continue;
                const content = await readFile(join(outputDir, file), "utf-8").catch(() => null);
                if (content != null) {
                    theirsSnapshot[file] = content;
                }
            }

            lockManager.markPatchResolved(patch.id, {
                patch_content: r.diff,
                content_hash: r.hash,
                base_generation: currentGen,
                files: filesForSnapshot,
                ...(Object.keys(theirsSnapshot).length > 0 ? { theirs_snapshot: theirsSnapshot } : {})
            });
            patchesResolved++;
        }

        lockManager.save();

        const committer = new ReplayCommitter(git, outputDir);
        await committer.stageAll();
        const commitSha = await committer.commitReplay(
            lock.patches.length,
            lock.patches,
            "[fern-replay] Resolved conflicts"
        );

        return {
            success: true,
            commitSha,
            phase: "committed",
            patchesResolved,
            patchesDismissed: patchesDismissed.length > 0 ? patchesDismissed : undefined,
            warnings: warnings.length > 0 ? warnings : undefined
        };
    }

    // ---- No unresolved/resolving patches → legacy behavior ----
    const committer = new ReplayCommitter(git, outputDir);
    await committer.stageAll();
    const commitSha = await committer.commitReplay(lock.patches.length, lock.patches);

    return { success: true, commitSha, phase: "committed" };
}

async function findConflictMarkerFiles(git: GitClient): Promise<string[]> {
    const output = await git.exec(["grep", "-l", "<<<<<<<", "--", "."]).catch(() => "");
    return output.trim() ? output.trim().split("\n").filter(Boolean) : [];
}

async function getChangedFiles(git: GitClient, currentGen: string, files: string[]): Promise<string[]> {
    const filesOutput = await git.exec(["diff", "--name-only", currentGen, "--", ...files]).catch(() => null);

    if (!filesOutput || !filesOutput.trim()) return files;

    const changed = filesOutput
        .trim()
        .split("\n")
        .filter(Boolean)
        .filter((f) => !f.startsWith(".fern/"));

    return changed.length > 0 ? changed : files;
}

/**
 * Parse `diff --git a/<X> b/<Y>` headers from a unified diff and return the
 * destination paths. For renames, the "to" side is preferred. Filters out
 * `.fern/` paths.
 */
function extractFilesFromDiff(unifiedDiff: string): string[] {
    const out = new Set<string>();
    const renameSources = new Set<string>();
    let currentTarget: string | null = null;

    for (const line of unifiedDiff.split("\n")) {
        const headerMatch = line.match(/^diff --git a\/(.+?) b\/(.+)$/);
        if (headerMatch) {
            currentTarget = headerMatch[2]!;
            if (!currentTarget.startsWith(".fern/")) out.add(currentTarget);
            continue;
        }
        if (currentTarget && line.startsWith("rename from ")) {
            renameSources.add(line.slice("rename from ".length));
        }
    }
    // Drop rename sources (we want the post-rename path, not the pre-rename path).
    for (const src of renameSources) out.delete(src);
    return Array.from(out);
}
